import { NavigationStart, Router } from '@angular/router';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { filter } from 'rxjs';

export type NavigationHistoryEntry = {
  url: string;
  queryParams?: { [key: string]: any } | any;
  fragment?: string;
  completeUrl: string;
};

export class NavigationHistory {
  /**
   * The history of the navigation.
   */
  private history: NavigationHistoryEntry[] = [];
  constructor(protected router: Router, protected localize: LocalizeRouterService) {
    this.router.events
      .pipe(filter((event: any) => event instanceof NavigationStart))
      .subscribe((event: NavigationStart) => {
        this.pushUrl(event.url);
      });
  }

  /**
   * Get the current url.
   */
  get currentUrl(): string | null {
    const entry = this.currentEntry;
    if (!entry) {
      return null;
    }
    let url = entry.url;
    if (entry.queryParams) {
      url +=
        '?' +
        Object.keys(entry.queryParams)
          .map((key) => `${key}=${entry.queryParams[key]}`)
          .join('&');
    }
    if (entry.fragment) {
      url += '#' + entry.fragment;
    }
    return url;
  }

  /**
   * Get the current navigation history entry.
   */
  get currentEntry(): NavigationHistoryEntry | undefined {
    return this.history[this.history.length - 1];
  }

  /**
   * Reset the history when the language is changed.
   */
  resetHistoryForChanginglanguage(): void {
    this.history = [];
    this.pushUrl(this.localize.translateRoute('/account').toString());
    this.pushCurrentUrl();
  }

  /**
   * Insert the current url into the history.
   */
  pushCurrentUrl(): void {
    this.pushUrl(document.location.pathname + document.location.search + document.location.hash);
  }

  /**
   * Insert a new url into the history.
   *
   * @param url - The url to insert into the history.
   */
  pushUrl(url: string): void {
    // avoid to replicate the last history items.
    if (this.history.length) {
      if (this.history[this.history.length - 1].completeUrl === url) {
        return;
      }
    }
    this.pushEntry(this.convertUrlToEntry(url));
  }

  /**
   * Insert a new entry into the history.
   *
   * @param entry - The entry to insert into the history.
   */
  pushEntry(entry: NavigationHistoryEntry): void {
    this.history.push(entry);
  }

  /**
   * Extract the last entry from the history.
   *
   * @returns - The last entry in the history.
   */
  pop(): NavigationHistoryEntry | undefined {
    return this.history.pop();
  }

  /**
   * Replace the current entry in the history with the given url.
   *
   * @param url - The url to replace the current entry with.
   */
  replaceCurrent(url: string) {
    if (this.history.length) {
      this.history[this.history.length - 1] = this.convertUrlToEntry(url);
    }
  }

  /**
   * Remove the last entry from the history.
   */
  removeLastentry(): void {
    this.history.pop();
  }

  /**
   * Navigate to the previous page in the history.
   *
   * @param fallBackUrl - The url to navigate to if there is no history.
   * @returns
   */
  navigateBack(fallBackUrl?: string): void {
    this.removeLastentry();
    const entry = this.history.pop();
    if (!entry) {
      fallBackUrl ? this.router.navigate([fallBackUrl]) : window.history.back();
      return;
    }
    this.router.navigate([entry.url], { queryParams: entry.queryParams, fragment: entry.fragment });
  }

  /**
   * Convert the url to an Entry for history.
   *
   * @param {string} url url to convert to entry.
   * @returns {NavigationHistory} The entry for the history.
   */
  private convertUrlToEntry(url: string): NavigationHistoryEntry {
    const urlWithFragment = url.split('#');
    const entry: NavigationHistoryEntry = { url: '', completeUrl: url };
    if (urlWithFragment.length > 1) {
      entry.fragment = urlWithFragment[1];
    }
    const urlWithParams = urlWithFragment[0].split('?');
    if (urlWithParams.length > 1) {
      entry.queryParams = urlWithParams[1]
        .split('&')
        .map((param) => param.split('='))
        .reduce((acc: any, curr) => {
          acc[curr[0]] = curr[1];
          return acc;
        }, {});
    }
    // WEB-4798 - decode the url for not to have the encoded url in the history.
    // e.g. arbeitspl%C3%A4tze -> arbeitsplätze
    entry.url = decodeURI(urlWithParams[0]);
    return entry;
  }
}
